// exercise schema + Bedrock tool definition (PLAN.md §6.1 Batch 5, D-18).
// Pure data only — safe to import from both the API (Lambda) and the web SPA.
// `appleWorkoutId` + Apple Health/Strava ingest (§6.1.5 #5) are Phase 3.
// `isPrivate` (DASHBOARD_DND_AND_ACTIVITY_PLAN.md D-ACT-4/6) excludes an entry
// from the AI analysis bundle and masks it in shared timeline views.

export const EXERCISE_KINDS = [
  "walking",
  "running",
  "cycling",
  "swimming",
  "yoga",
  "weights",
  "sport",
  "hiking",
  "pilates",
  "intimacy",
  "other",
] as const;
export type ExerciseKind = (typeof EXERCISE_KINDS)[number];

export const EXERCISE_KIND_LABELS: Record<ExerciseKind, string> = {
  walking: "Walking",
  running: "Running",
  cycling: "Cycling",
  swimming: "Swimming",
  yoga: "Yoga",
  weights: "Weights",
  sport: "Sport",
  hiking: "Hiking",
  pilates: "Pilates",
  intimacy: "Intimacy",
  other: "Other",
};

export const EXERCISE_INTENSITIES = ["low", "moderate", "vigorous"] as const;
export type ExerciseIntensity = (typeof EXERCISE_INTENSITIES)[number];

/** Payload for an `exercise` event (PLAN.md §6.1 Batch 5). */
export interface ExercisePayload {
  startedAt: string; // R — ISO-8601 with offset (auto-now if only duration given)
  endedAt: string; // R — derived from startedAt + durationMin when not stated
  kind: ExerciseKind; // R
  durationMin: number; // R — user-stated or derived from start/end
  intensity?: ExerciseIntensity; // O
  distanceMi?: number; // O
  caloriesEstimate?: number; // O
  avgHr?: number; // O
  maxHr?: number; // O
  perceivedExertionRpe?: number; // O — 1..10 RPE
  source?: string; // A — "chat" (default) | "apple_health" | "strava" (Phase 3+)
  notes?: string; // O
  /** NEW (D-ACT-4/6). Excluded from the AI analysis bundle and masked in shared
   *  timeline views. Display/analysis control only — NOT an access control:
   *  anyone who can open this patient can reveal it on the Activity page. */
  isPrivate?: boolean;
}

/** NEW. Generic label shown in place of a private entry's real summary. */
export const PRIVATE_ACTIVITY_LABEL = "Private activity";

/** NEW. True when an event's payload carries the private flag. Kind-agnostic so
 *  a future event type can adopt the same flag without changing callers. */
export function isPrivateEvent(e: { payload: Record<string, unknown> }): boolean {
  return e.payload?.isPrivate === true;
}

/**
 * Bedrock tool: record an exercise / workout session. Needs the kind AND a
 * duration in minutes. If the user gave start/end times, the server derives the
 * duration; if they gave only a duration, omit times (the server stamps now).
 */
export const RECORD_EXERCISE_TOOL = {
  name: "record_exercise",
  description:
    "Record an exercise or workout session. Call this once you have the kind of activity AND a " +
    "duration in minutes (or start + end times the duration can be derived from). Suggest an " +
    "intensity (low/moderate/vigorous) from the kind and duration if the user didn't state one. " +
    "Omit startedAt unless the user said when they exercised. Use kind 'intimacy' for sexual " +
    "activity; it is recorded as physical activity and is marked private automatically.",
  input_schema: {
    type: "object",
    properties: {
      kind: { type: "string", enum: [...EXERCISE_KINDS] },
      durationMin: { type: "number", description: "Duration in minutes." },
      startedAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if no time was given." },
      endedAt: { type: "string", description: "ISO-8601 with UTC offset, if the user gave an end time." },
      intensity: { type: "string", enum: [...EXERCISE_INTENSITIES] },
      distanceMi: { type: "number", description: "Distance in miles, for distance-based activities." },
      caloriesEstimate: { type: "number" },
      perceivedExertionRpe: { type: "number", description: "Rate of perceived exertion, 1–10." },
      notes: { type: "string" },
      isPrivate: {
        type: "boolean",
        description:
          "Set true only if the user explicitly asked for this entry to be private. " +
          "Entries with kind 'intimacy' are marked private automatically — you do not " +
          "need to set this for them.",
      },
    },
    required: ["kind", "durationMin"],
  },
} as const;
